import { useUserV2 } from "hooks"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useMemo, useState } from "react"
import { navigateTo } from "utils/navigateTo"
import { getQuizPath } from "../lib/utils"

interface QuizPages {
  previousPage: string
  nextPage: string
  getQuizPath: (uri?: string) => string
  startQuiz: () => void
}

export const useQuizNavigation = (numberOfQuestions: number): QuizPages => {
  const user = useUserV2()
  const path = usePathname()

  const [previousPage, setPreviousPage] = useState<string>(getQuizPath())
  const [nextPage, setNextPage] = useState<string>(getQuizPath("0"))

  const asPath = path?.startsWith("/us/") ? path.replace("/us/", "/") : path

  const question = path?.split("/").pop() ?? "0"

  const startQuiz = useCallback(() => {
    navigateTo(getQuizPath("0"))
  }, [])

  useEffect(() => {
    const isQuestion =
      question.length !== 0 && [...question].every((c) => c >= "0" && c <= "9")

    // Landing page
    if (asPath === getQuizPath()) {
      setPreviousPage(getQuizPath())
      setNextPage(getQuizPath("0"))
      return
    }

    if (isQuestion) {
      const questionNum = Number(question)

      switch (questionNum) {
        // first question
        case 0:
          setPreviousPage(getQuizPath())
          setNextPage(getQuizPath("1"))
          break
        // last question
        case numberOfQuestions - 1:
          setPreviousPage(getQuizPath(`${questionNum - 1}`))
          if (user.isLoggedIn) setNextPage(getQuizPath("analysis"))
          else {
            setNextPage(getQuizPath("email"))
          }
          break
        // all in-between
        default:
          setPreviousPage(getQuizPath(`${questionNum - 1}`))
          setNextPage(getQuizPath(`${questionNum + 1}`))
      }

      return
    }

    if (asPath === getQuizPath("email")) {
      setPreviousPage(getQuizPath(`${numberOfQuestions}`))
      setNextPage(getQuizPath("analysis"))
      return
    }

    if (asPath === getQuizPath("analysis")) {
      const page = "result"

      return setNextPage(getQuizPath(page))
    }
  }, [asPath, question, user.isLoggedIn, numberOfQuestions])

  return useMemo(
    () => ({
      previousPage,
      nextPage,
      getQuizPath,
      startQuiz,
    }),
    [previousPage, nextPage, startQuiz]
  )
}
